import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { AppBar as MuiAppBar, Toolbar, IconButton } from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";

import { getEmployeeRequest } from "../../../store/Employees/actions";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import messages from "../../../assets/locale/messages";
import LangSwitcher from "../../LangSwitcher";
import ClientCompanySwitcher from "../../Clients/ClientCompanySwitcher";
import { UIAvatar } from "../../ui";
import NotificationsMenu from "../../NotificationsMenu";
import NotificationItem from "../../Notifications/NotificationItem";
import NotificationsEmpty from "../../Notifications/NotificationsEmpty";
import { getDropdownNotificationsRequest } from "../../../store/Notifications/actions";
import {
  getCompanyDropdownNotificationsRequest,
  setCompanyUnseenNotificationsCount,
} from "../../../store/CompanyPortal/Notifications/actions";
import {
  getClientDropdownNotificationsRequest,
  setClientUnseenNotificationsCount,
} from "../../../store/ClientPortal/Notifications/actions";
import { handleNotificationsClickActions } from "../../../firebase/helpers";
import { handleCompanyPortalNotificationClick } from "../../../utils/companyPortalNotifications";
import { handleClientPortalNotificationClick } from "../../../utils/clientPortalNotifications";
import { SeenNotificationIcon } from "../../../utils/Icons";
import { isAdmin, isSuperAdmin, isSupervisorManger } from "../../../utils/Permissions";
import { getPermissionRequest } from "../../../store/Auth/actions";
import { getSessionPortal } from "../../../utils/Auth";
import { AUTH_CONTEXT_TYPE } from "../../../utils/authPortal";
import { fetchCompanyPortalCurrentUser } from "../../../network/apis/companyPortalRegistration";
import { getClientPortalCurrentUser } from "../../../network/apis/clientPortalAuth";
import { persistCompanyPortalSession } from "../../../utils/companyRegistration";
import "./NavBar.scss";

const NavBar = ({
  sideMenuOpen,
  drawerWidth,
  collapsedDrawerWidth,
  openMobileSideMenu,
  children,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userType = localStorage.getItem("userType");

  const isCompanyPortal = getSessionPortal() === AUTH_CONTEXT_TYPE.COMPANY;
  const isClientPortal = getSessionPortal() === AUTH_CONTEXT_TYPE.CLIENT;
  
  const lang = useSelector((state) => state.locale.lang);
  const userRoleName = useSelector((state) => state.auth.login?.role?.name);
  const superVisorInfo = useSelector((state) => state.auth.login);

  const userLoginId = useSelector((state) => state.auth.login?.id);
  const employee = useSelector((state) => state.employees.employee);
  const user_id = useSelector((state) =>
    isCompanyPortal || isClientPortal
      ? state.auth.login?.id
      : state.auth.login?.role?.id,
  );

  const company = superVisorInfo?.company;
  /** Company portal: prefer company logo/name; fall back to the logged-in user. */
  const companyPortalDisplayName = company
    ? (lang === "ar"
        ? company.name_ar || company.name_en || company.name
        : company.name_en || company.name
      )?.trim() || superVisorInfo?.name || ""
    : superVisorInfo?.name || "";

  const avatarImgSrc = isCompanyPortal
    ? company?.image_url || superVisorInfo?.image_url
    : isClientPortal
      ? superVisorInfo?.image_url
      : userType === "admin"
        ? isSuperAdmin()
          ? superVisorInfo?.image_url
          : employee?.image_url
        : superVisorInfo?.image || superVisorInfo?.image_url;

  const avatarName = isCompanyPortal
    ? companyPortalDisplayName || superVisorInfo?.name || ""
    : isClientPortal
      ? superVisorInfo?.name ||
        `${superVisorInfo?.first_name_en ?? ""} ${
          superVisorInfo?.last_name_en ?? ""
        }`.trim()
      : userType === "admin"
        ? isSuperAdmin()
          ? userRoleName
          : employee?.name
        : superVisorInfo?.name ||
          `${superVisorInfo?.first_name_en ?? ""} ${
            superVisorInfo?.last_name_en ?? ""
          }`.trim();

  const notificationsList = useSelector(
    (state) => state?.notifications?.dropdownNotifications
  );
  const unseenNotificationsCount = useSelector(
    (state) => state.notifications.unseenNotificationsCount
  );

  const companyNotificationsList = useSelector(
    (state) => state?.companyNotifications?.dropdownNotifications
  );
  const companyUnseenNotificationsCount = useSelector(
    (state) => state.companyNotifications.unseenNotificationsCount
  );
  /** Badge count seeded from login / GET me (`company_portal_unseen_notifications_count`). */
  const companyLoginUnseenCount =
    superVisorInfo?.company_portal_unseen_notifications_count;

  const clientNotificationsList = useSelector(
    (state) => state?.clientNotifications?.dropdownNotifications
  );
  const clientUnseenNotificationsCount = useSelector(
    (state) => state.clientNotifications.unseenNotificationsCount
  );
  /** Badge count seeded from login / GET /clients/me (`client_portal_unseen_notifications_count`). */
  const clientLoginUnseenCount =
    superVisorInfo?.client_portal_unseen_notifications_count;

  const { notifications } = messages[lang];

  useEffect(() => {
    if (!isCompanyPortal) return;
    dispatch(setCompanyUnseenNotificationsCount(companyLoginUnseenCount || 0));
  }, [isCompanyPortal, companyLoginUnseenCount, dispatch]);

  useEffect(() => {
    if (!isClientPortal) return;
    dispatch(setClientUnseenNotificationsCount(clientLoginUnseenCount || 0));
  }, [isClientPortal, clientLoginUnseenCount, dispatch]);

  useEffect(() => {
    if (
      getSessionPortal() === AUTH_CONTEXT_TYPE.COMPANY ||
      getSessionPortal() === AUTH_CONTEXT_TYPE.CLIENT ||
      user_id == null ||
      isSuperAdmin() ||
      isAdmin() ||
      isSupervisorManger()
    ) {
      return;
    }
    dispatch(getPermissionRequest({ id: user_id }));
  }, [lang, user_id, dispatch]);

  const handleGetNotifications = () => {
    dispatch(
      getDropdownNotificationsRequest({
        items: 5,
        page: 1,
      })
    );
  };

  /** Header label: "N unread" while unread remain, "All read" once cleared, else "0 unread". */
  const getUnreadLabel = (count, hasItems) => {
    if (count > 0) return notifications.unreadCount.replace("{count}", count);
    if (hasItems) return notifications.allRead;
    return notifications.unreadCount.replace("{count}", 0);
  };

  const renderMenuHeader = (count, hasItems) => (
    <>
      <span className="notifications-menu-header__title">
        {notifications.title}
      </span>
      <span className="notifications-menu-header__count">
        {getUnreadLabel(count, hasItems)}
      </span>
    </>
  );

  const renderNotificationsEmpty = () => (
    <NotificationsEmpty
      title={notifications.allCaughtUp}
      description={notifications.emptyDescription}
    />
  );

  const renderNotificationContent = (notification) => (
    <NotificationItem
      variant="dropdown"
      title={notification.title}
      body={notification.body}
      createdAt={notification.created_at}
      isSeen={notification.is_seen}
    />
  );

  const getMenuItems = () =>
    notificationsList.map((notification) => ({
      id: notification.id,
      content: renderNotificationContent(notification),
      className: notification.is_seen
        ? "seen-notification"
        : "unseen-notification",
    }));

  const renderMenuController = () => (
    <div
      className="nav-bar-icon-button nav-bar-notification-button pointer"
      onClick={handleGetNotifications}
    >
      <div className="position-relative notification-icon-wrapper">
        {unseenNotificationsCount ? (
          <div
            className={`notifications-count position-absolute text-center`}
          ></div>
        ) : null}
        <SeenNotificationIcon />
      </div>
    </div>
  );
  const renderMenuFooter = () => (
    <div className="py-4  text-center">
      <Link to={ROUTE_PATHS.notifications}>
        <p className="see-all-btn fsize-14 pointer mb-0">
          {notifications.seeAllNotifications}
        </p>
      </Link>
    </div>
  );

  const handleSelectNotification = (notificationId) => {
    const selectedNotification = notificationsList.find(
      (item) => item.id === notificationId
    );

    handleNotificationsClickActions(selectedNotification);
  };

  const handleGetCompanyNotifications = () => {
    dispatch(getCompanyDropdownNotificationsRequest({ items: 5, page: 1 }));
  };

  const getCompanyMenuItems = () =>
    companyNotificationsList.map((notification) => ({
      id: notification.id,
      content: renderNotificationContent(notification),
      className: notification.is_seen
        ? "seen-notification"
        : "unseen-notification",
    }));

  const renderCompanyMenuController = () => (
    <div
      className="nav-bar-icon-button nav-bar-notification-button pointer"
      onClick={handleGetCompanyNotifications}
    >
      <div className="position-relative notification-icon-wrapper">
        {companyUnseenNotificationsCount ? (
          <div
            className={`notifications-count position-absolute text-center`}
          ></div>
        ) : null}
        <SeenNotificationIcon />
      </div>
    </div>
  );

  const renderCompanyMenuFooter = () => (
    <div className="py-4  text-center">
      <Link to={ROUTE_PATHS.companyNotifications}>
        <p className="see-all-btn fsize-14 pointer mb-0">
          {notifications.seeAllNotifications}
        </p>
      </Link>
    </div>
  );

  const handleSelectCompanyNotification = (notificationId) => {
    const selectedNotification = companyNotificationsList.find(
      (item) => item.id === notificationId
    );
    handleCompanyPortalNotificationClick(selectedNotification);
  };

  const handleGetClientNotifications = () => {
    dispatch(getClientDropdownNotificationsRequest({ items: 5, page: 1 }));
  };

  const getClientMenuItems = () =>
    clientNotificationsList.map((notification) => ({
      id: notification.id,
      content: renderNotificationContent(notification),
      className: notification.is_seen
        ? "seen-notification"
        : "unseen-notification",
    }));

  const renderClientMenuController = () => (
    <div
      className="nav-bar-icon-button nav-bar-notification-button pointer"
      onClick={handleGetClientNotifications}
    >
      <div className="position-relative notification-icon-wrapper">
        {clientUnseenNotificationsCount ? (
          <div
            className={`notifications-count position-absolute text-center`}
          ></div>
        ) : null}
        <SeenNotificationIcon />
      </div>
    </div>
  );

  const renderClientMenuFooter = () => (
    <div className="py-4  text-center">
      <Link to={ROUTE_PATHS.clientNotifications}>
        <p className="see-all-btn fsize-14 pointer mb-0">
          {notifications.seeAllNotifications}
        </p>
      </Link>
    </div>
  );

  const handleSelectClientNotification = (notificationId) => {
    const selectedNotification = clientNotificationsList.find(
      (item) => item.id === notificationId
    );
    handleClientPortalNotificationClick(selectedNotification);
  };

  useEffect(() => {
    if (!isSuperAdmin() && userType === "admin") {
      dispatch(getEmployeeRequest(userLoginId));
    }
  }, [userLoginId, userType, dispatch]);

  /** GET /company_users/me — refresh company portal user in Redux + storage (Bearer). */
  useEffect(() => {
    if (
      getSessionPortal() !== AUTH_CONTEXT_TYPE.COMPANY ||
      !userLoginId ||
      superVisorInfo?.must_change_password
    ) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchCompanyPortalCurrentUser();
        if (cancelled) return;
        persistCompanyPortalSession(me);
      } catch {
        /* 401 → axios interceptor; other errors keep existing session */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userLoginId, superVisorInfo?.must_change_password]);

  /** GET /clients/me — refresh the client-portal unseen notifications badge. */
  useEffect(() => {
    if (!isClientPortal || !userLoginId || superVisorInfo?.must_change_password) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getClientPortalCurrentUser();
        if (cancelled) return;
        const count =
          res?.data?.data?.client_portal_unseen_notifications_count ?? 0;
        dispatch(setClientUnseenNotificationsCount(count));
      } catch {
        /* 401 → axios interceptor; other errors keep the login-time count */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isClientPortal, userLoginId, superVisorInfo?.must_change_password, dispatch]);

  const customStyle = {
    backgroundColor: "var(--color-background-default)",
    transition: (theme) =>
      theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    width: { lg: `calc(100% - ${collapsedDrawerWidth}px)` },
    ...(sideMenuOpen && {
      ml: { lg: `${drawerWidth}px` },
      width: { lg: `calc(100% - ${drawerWidth}px)` },
      transition: (theme) =>
        theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
    }),
  };

  return (
    <MuiAppBar position="fixed" sx={customStyle} className="nav-bar">
      <Toolbar className="">
        <IconButton
          color="primary"
          aria-label="open drawer"
          edge="start"
          onClick={() => openMobileSideMenu(true)}
          sx={{ display: { lg: "none" } }}
          className="mobile-toggle-icon"
        >
          <MenuIcon />
        </IconButton>
        <div className="w-100 d-flex justify-content-between align-items-center nav-bar-content">
          <div id="nav-bar-breadcrumb-slot" className="nav-bar-breadcrumb-slot" />
          <div className="d-flex flex-sm-row justify-content-end align-items-center nav-items-container">
            <div className="d-flex justify-content-between align-items-center nav-icons-container">
              {children ? <div className="nav-bar-extra">{children}</div> : null}
              {/* Page-owned navbar action (see NavBarPortal slot="action"). */}
              <div id="nav-bar-action-slot" className="nav-bar-action-slot" />
              {isClientPortal ? <ClientCompanySwitcher /> : null}
              {userType == "admin" && (
                <NotificationsMenu
                  menuController={renderMenuController()}
                  menuHeader={renderMenuHeader(
                    (notificationsList || []).filter((n) => !n.is_seen).length,
                    (notificationsList?.length || 0) > 0
                  )}
                  menuItems={getMenuItems()}
                  handleSelectItem={handleSelectNotification}
                  menuFooter={renderMenuFooter()}
                  emptyState={renderNotificationsEmpty()}
                />
              )}
              {isCompanyPortal && (
                <NotificationsMenu
                  menuController={renderCompanyMenuController()}
                  menuHeader={renderMenuHeader(
                    (companyNotificationsList || []).filter((n) => !n.is_seen)
                      .length,
                    (companyNotificationsList?.length || 0) > 0
                  )}
                  menuItems={getCompanyMenuItems()}
                  handleSelectItem={handleSelectCompanyNotification}
                  menuFooter={renderCompanyMenuFooter()}
                  emptyState={renderNotificationsEmpty()}
                />
              )}
              {isClientPortal && (
                <NotificationsMenu
                  menuController={renderClientMenuController()}
                  menuHeader={renderMenuHeader(
                    (clientNotificationsList || []).filter((n) => !n.is_seen)
                      .length,
                    (clientNotificationsList?.length || 0) > 0
                  )}
                  menuItems={getClientMenuItems()}
                  handleSelectItem={handleSelectClientNotification}
                  menuFooter={renderClientMenuFooter()}
                  emptyState={renderNotificationsEmpty()}
                />
              )}
              <div className="nav-bar-icon-button nav-bar-lang-button">
                <LangSwitcher internalSwitcher />
              </div>
              <div
                className={`nav-bar-avatar-trigger d-flex flex-column align-items-center ${
                  (isCompanyPortal ||
                    isClientPortal ||
                    (!isSuperAdmin() && userType === "admin")) &&
                  "pointer"
                }`}
                onClick={() => {
                  if (isClientPortal) {
                    history.push(ROUTE_PATHS.clientPortalDashboard);
                    return;
                  }
                  if (isCompanyPortal) {
                    history.push(ROUTE_PATHS.companySettingsProfile);
                    return;
                  }
                  if (!isSuperAdmin() && userType === "admin") {
                    history.push(ROUTE_PATHS["profile"]);
                  }
                }}
              >
                <UIAvatar
                  src={avatarImgSrc}
                  alt="user-profile"
                  name={avatarName}
                  size="sm"
                  className="nav-bar-avatar"
                />
              </div>
            </div>
          </div>
        </div>
      </Toolbar>
    </MuiAppBar>
  );
};

NavBar.propTypes = {
  sideMenuOpen: PropTypes.bool.isRequired,
  drawerWidth: PropTypes.number.isRequired,
  collapsedDrawerWidth: PropTypes.number.isRequired,
  openMobileSideMenu: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default NavBar;
